/**
 * The one SubResource-to-cached-texture walk every procedural texture slice runs.
 * Each slice supplies its type name and rasteriser. The table is the owning
 * file's, so a material `.tres` passes its own `subResources` as a scene does.
 */
import { findSubResource, parseResourceReference } from '../SubResourceResolver';
import { proceduralTexture, proceduralTextureKey } from './proceduralTextureCache';
import { unlessAllocationFails } from './pixelAllocation';
/**
 * Resolves `ref` as an inline `[sub_resource]` of `typeName`, rasterised once per
 * file and sub-resource. A `variant` joins the cache key for a raster that also depends on a
 * resource outside the file. Null for every other reference form, which leaves the
 * caller's async path untouched, and for pixels the tab cannot allocate: a
 * GradientTexture2D at Godot's 16384² ceiling is 1 GiB of RGBA8. The result is
 * borrowed: never dispose it.
 */
export function resolveProceduralSubResource(ref, internalResources, typeName, rasterize, variant) {
    const resource = findTypedSubResource(ref, internalResources, typeName);
    if (!resource)
        return null;
    // The cache holds plain THREE.Texture, but a given sub-resource id has one
    // type, so whatever it holds under this key came out of this same rasterize.
    const cacheId = variant ? `${resource.id}:${variant}` : resource.id;
    const texture = proceduralTexture(internalResources, cacheId, () => unlessAllocationFails(allocationLabel(typeName, resource.id), () => rasterize(resource.data, internalResources)));
    if (!texture)
        return null;
    return { texture, key: proceduralTextureKey(internalResources, cacheId) };
}
/** The inline `[sub_resource]` of `typeName` that `ref` names. Null for every other reference form. */
export function findTypedSubResource(ref, internalResources, typeName) {
    const parsed = parseResourceReference(ref ?? '');
    if (!parsed || parsed.type !== 'SubResource')
        return null;
    const resource = findSubResource(internalResources, parsed.id);
    return resource?.type === typeName ? resource : null;
}
/** How an allocation failure names the sub-resource it could not build. */
export function allocationLabel(typeName, id) {
    return `[${typeName}] sub-resource "${id}"`;
}
//# sourceMappingURL=resolveProceduralSubResource.js.map